/**
 * CustomerNumberField — edit cell for a client's Customer #.
 *
 * iter245 behavior (user request):
 *  • The AR "Request #" email flow is REMOVED.
 *  • Only Ryan Van Pelt or Mary Atkins may add/change the number
 *    (backend enforces the same on PATCH /clients/{id}/customer-number).
 *  • Everyone else sees the number (or an em-dash) read-only.
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import FoundationRefPicker from "@/components/integrations/FoundationRefPicker";
import { getFoundationRefRows } from "@/lib/api";

export const CUSTOMER_NUMBER_ALLOWED_EMAILS = ["ryan.vanpelt@absinc.com", "mary.atkins@absinc.com"];

export function CustomerNumberField({ value, client, onSet, disabled }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  // Once the Foundation customers list has synced, picking is the default — typing stays available.
  const [synced, setSynced] = useState(null);
  const [manual, setManual] = useState(false);

  const canEdit = CUSTOMER_NUMBER_ALLOWED_EMAILS.includes((user?.email || "").toLowerCase()) || user?.is_bypass;

  const beginEdit = () => {
    setDraft(value || "");
    setManual(false);
    setEditing(true);
    if (synced === null) getFoundationRefRows("customers", "").then(r => setSynced(!!r.data.synced_once)).catch(() => setSynced(false));
  };

  const save = () => {
    if (!draft.trim()) { toast.error("Customer # cannot be blank"); return; }
    onSet(draft.trim());
    setEditing(false);
  };

  if (!editing) {
    // Locked sheet OR non-Ryan user with a value: static display only.
    if (disabled || !canEdit) {
      return (
        <div className="w-full md:w-28 h-8 px-2 text-xs font-mono text-center bg-slate-100 border border-slate-200 rounded flex items-center justify-center text-slate-500" data-testid="customer-number-locked">
          {value || "—"}
        </div>
      );
    }
    if (value) {
      return (
        <div className="flex items-center gap-1 w-full md:w-28" data-testid="customer-number-set">
          <div className="flex-1 h-8 px-2 text-xs font-mono text-center bg-slate-50 border border-slate-200 rounded flex items-center justify-center text-slate-800">
            {value}
          </div>
          <Button type="button" size="sm" variant="ghost" className="h-8 px-1.5 text-[10px] text-slate-400 hover:text-slate-700" onClick={beginEdit} data-testid="btn-edit-customer-number">
            <Edit2 className="w-3 h-3" />
          </Button>
        </div>
      );
    }
    return (
      <Button type="button" size="sm" variant="outline" className="w-full md:w-28 h-8 text-[10px] text-blue-600 border-blue-300 hover:bg-blue-50" onClick={beginEdit} data-testid="btn-set-customer-number" disabled={!client}>
        Set #
      </Button>
    );
  }

  if (synced && !manual) {
    return (
      <div className="flex flex-col gap-1 w-full md:w-40" data-testid="customer-number-edit">
        <FoundationRefPicker listId="customers" value={draft || null} allowClear={false} placeholder="Pick Foundation customer…"
                             onChange={(code) => { setDraft(code); onSet(code); setEditing(false); }} testId="customer-number-picker" />
        <div className="flex items-center justify-between gap-1">
          <button type="button" className="text-[10px] text-blue-600 hover:underline whitespace-nowrap" onClick={() => setManual(true)} data-testid="btn-customer-number-manual">
            Type the # instead
          </button>
          <Button type="button" size="sm" variant="ghost" className="h-6 px-1 text-slate-400" onClick={() => setEditing(false)} data-testid="btn-cancel-customer-number">
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 w-full md:w-32" data-testid="customer-number-edit">
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        placeholder="New #"
        className="h-8 text-xs font-mono text-center"
        data-testid="input-customer-number-new"
      />
      <Button type="button" size="sm" className="h-8 px-1.5 bg-green-600 hover:bg-green-700 text-white" onClick={save} data-testid="btn-save-customer-number">
        <Check className="w-3 h-3" />
      </Button>
      <Button type="button" size="sm" variant="ghost" className="h-8 px-1.5 text-slate-400" onClick={() => setEditing(false)} data-testid="btn-cancel-customer-number">
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}
